/* eslint-disable react/no-unescaped-entities */
import { useRef, useState } from "react";
import { Camera, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Resize + JPEG-compress an image File to ~maxBytes, returning a data URL.
async function compressImage(file, { maxWidth = 800, quality = 0.78, maxBytes = 180000 } = {}) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const ratio = Math.min(1, maxWidth / img.width);
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        // Iteratively reduce quality until under target
        let q = quality;
        let dataUrl = canvas.toDataURL("image/jpeg", q);
        while (dataUrl.length > maxBytes && q > 0.3) {
          q -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", q);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = ev.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PhotoCapture({ value, onChange, testidPrefix = "animal-photo" }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Fichier non supporté", { description: "Veuillez choisir une image." });
      return;
    }
    setLoading(true);
    try {
      const dataUrl = await compressImage(file);
      onChange(dataUrl);
      toast.success("Photo ajoutée");
    } catch (e) {
      toast.error("Impossible de charger la photo");
    } finally {
      setLoading(false);
      if (camRef.current) camRef.current.value = "";
      if (galRef.current) galRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        {/* Preview */}
        <div
          className="relative w-24 h-24 rounded-xl border-2 border-dashed border-[#E2E4DF] bg-[#F0F0EB] grid place-items-center overflow-hidden flex-shrink-0"
          data-testid={`${testidPrefix}-preview`}
        >
          {loading ? (
            <Loader2 size={20} className="animate-spin text-[#5C6B5C]" />
          ) : value ? (
            <>
              <img src={value} alt="aperçu" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => onChange(null)}
                data-testid={`${testidPrefix}-remove`}
                className="absolute top-1 end-1 w-6 h-6 rounded-full bg-red-600 text-white grid place-items-center hover:bg-red-700"
                title="Supprimer la photo"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            <ImageIcon size={28} className="text-[#5C6B5C]/40" />
          )}
        </div>

        {/* Actions */}
        <div className="flex-1 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => camRef.current?.click()}
            disabled={loading}
            data-testid={`${testidPrefix}-camera-btn`}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-[#1C3A27]/30 bg-white text-[#1C3A27] text-sm font-semibold hover:bg-[#1C3A27] hover:text-white transition-colors disabled:opacity-50"
          >
            <Camera size={16} />
            Photo
          </button>
          <button
            type="button"
            onClick={() => galRef.current?.click()}
            disabled={loading}
            data-testid={`${testidPrefix}-gallery-btn`}
            className="flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-[#1C3A27]/30 bg-white text-[#1C3A27] text-sm font-semibold hover:bg-[#1C3A27] hover:text-white transition-colors disabled:opacity-50"
          >
            <ImageIcon size={16} />
            Galerie
          </button>
          <p className="col-span-2 text-[11px] text-[#5C6B5C] leading-tight">
            Astuce : sur mobile, "Photo" ouvre votre appareil photo. "Galerie" choisit une photo existante.
          </p>
        </div>
      </div>

      {/* Hidden file inputs */}
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testidPrefix}-camera-input`}
      />
      <input
        ref={galRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testidPrefix}-gallery-input`}
      />
    </div>
  );
}
